/**
 * Maps a country name to a flag emoji.
 *
 * The AI returns plain country names (e.g. "Italy", "Czech Republic"). We map
 * those — plus a few common aliases — to ISO 3166-1 alpha-2 codes, then turn the
 * code into a flag emoji built from regional-indicator symbols.
 *
 * Coverage is intentionally partial: countries we don't recognise simply get no
 * flag, and callers fall back to showing the country name. Add entries as needed.
 */

/** Country name (lowercased) -> ISO 3166-1 alpha-2 code. */
const NAME_TO_ISO2: Record<string, string> = {
	// Europe
	'albania': 'AL', 'andorra': 'AD', 'austria': 'AT', 'belarus': 'BY',
	'belgium': 'BE', 'bosnia and herzegovina': 'BA', 'bosnia': 'BA',
	'bulgaria': 'BG', 'croatia': 'HR', 'cyprus': 'CY',
	'czech republic': 'CZ', 'czechia': 'CZ', 'denmark': 'DK', 'estonia': 'EE',
	'finland': 'FI', 'france': 'FR', 'germany': 'DE', 'greece': 'GR',
	'hungary': 'HU', 'iceland': 'IS', 'ireland': 'IE', 'italy': 'IT',
	'kosovo': 'XK', 'latvia': 'LV', 'liechtenstein': 'LI', 'lithuania': 'LT',
	'luxembourg': 'LU', 'malta': 'MT', 'moldova': 'MD', 'monaco': 'MC',
	'montenegro': 'ME', 'netherlands': 'NL', 'the netherlands': 'NL',
	'north macedonia': 'MK', 'macedonia': 'MK', 'norway': 'NO', 'poland': 'PL',
	'portugal': 'PT', 'romania': 'RO', 'russia': 'RU', 'san marino': 'SM',
	'serbia': 'RS', 'slovakia': 'SK', 'slovenia': 'SI', 'spain': 'ES',
	'sweden': 'SE', 'switzerland': 'CH', 'ukraine': 'UA',
	'united kingdom': 'GB', 'uk': 'GB', 'great britain': 'GB', 'britain': 'GB',
	'england': 'GB', 'scotland': 'GB', 'wales': 'GB', 'vatican city': 'VA',

	// Americas
	'argentina': 'AR', 'bolivia': 'BO', 'brazil': 'BR', 'canada': 'CA',
	'chile': 'CL', 'colombia': 'CO', 'costa rica': 'CR', 'cuba': 'CU',
	'dominican republic': 'DO', 'ecuador': 'EC', 'el salvador': 'SV',
	'guatemala': 'GT', 'honduras': 'HN', 'jamaica': 'JM', 'mexico': 'MX',
	'nicaragua': 'NI', 'panama': 'PA', 'paraguay': 'PY', 'peru': 'PE',
	'puerto rico': 'PR', 'united states': 'US', 'united states of america': 'US',
	'usa': 'US', 'us': 'US', 'uruguay': 'UY', 'venezuela': 'VE',

	// Asia
	'afghanistan': 'AF', 'armenia': 'AM', 'azerbaijan': 'AZ', 'bahrain': 'BH',
	'bangladesh': 'BD', 'bhutan': 'BT', 'cambodia': 'KH', 'china': 'CN',
	'georgia': 'GE', 'hong kong': 'HK', 'india': 'IN', 'indonesia': 'ID',
	'iran': 'IR', 'iraq': 'IQ', 'israel': 'IL', 'japan': 'JP', 'jordan': 'JO',
	'kazakhstan': 'KZ', 'kuwait': 'KW', 'kyrgyzstan': 'KG', 'laos': 'LA',
	'lebanon': 'LB', 'malaysia': 'MY', 'maldives': 'MV', 'mongolia': 'MN',
	'myanmar': 'MM', 'nepal': 'NP', 'north korea': 'KP', 'oman': 'OM',
	'pakistan': 'PK', 'palestine': 'PS', 'philippines': 'PH', 'qatar': 'QA',
	'saudi arabia': 'SA', 'singapore': 'SG', 'south korea': 'KR', 'korea': 'KR',
	'sri lanka': 'LK', 'syria': 'SY', 'taiwan': 'TW', 'tajikistan': 'TJ',
	'thailand': 'TH', 'turkey': 'TR', 'türkiye': 'TR', 'turkmenistan': 'TM',
	'united arab emirates': 'AE', 'uae': 'AE', 'uzbekistan': 'UZ',
	'vietnam': 'VN', 'yemen': 'YE',

	// Africa
	'algeria': 'DZ', 'angola': 'AO', 'botswana': 'BW', 'cameroon': 'CM',
	'egypt': 'EG', 'ethiopia': 'ET', 'ghana': 'GH', 'kenya': 'KE',
	'libya': 'LY', 'madagascar': 'MG', 'morocco': 'MA', 'mozambique': 'MZ',
	'namibia': 'NA', 'nigeria': 'NG', 'rwanda': 'RW', 'senegal': 'SN',
	'south africa': 'ZA', 'tanzania': 'TZ', 'tunisia': 'TN', 'uganda': 'UG',
	'zambia': 'ZM', 'zimbabwe': 'ZW',

	// Oceania
	'australia': 'AU', 'fiji': 'FJ', 'new zealand': 'NZ',
};

/** Converts an ISO 3166-1 alpha-2 code into its flag emoji. */
function codeToFlag(iso2: string): string {
	return iso2
		.toUpperCase()
		.replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

/**
 * Returns the flag emoji for a country name, or '' if we don't recognise it.
 * Callers should fall back to displaying the country name when this is empty.
 */
export function getCountryFlag(country: string | undefined | null): string {
	if (!country) return '';
	const iso2 = NAME_TO_ISO2[country.trim().toLowerCase()];
	return iso2 ? codeToFlag(iso2) : '';
}
